import {
  ATTENTION_OPERATORS_BY_TYPE,
  OPERATORS_BY_SOURCE_TYPE,
  type AttentionRuleDraft,
  type ConditionOperator,
  type FormDraft,
  type QuestionType,
  type SaveConditionInput,
  type SaveDraftInput,
} from '../types/forms';

// Fase 23A: modelo local (pura) del constructor de formularios. Separado del componente React para
// poder probar la logica reusable (mapeos, reordenamiento, armado del payload, origenes validos de
// condicion) sin renderizar UI, siguiendo el patron de tests de utils del proyecto.

export interface EditOption { key: string; logicalKey?: string; label: string }
// Fase 23C: regla de atension editable por pregunta (null = sin regla). valueOptionKey referencia una
// opcion de la MISMA pregunta por su key local.
export interface EditAttention { operator: ConditionOperator; valueOptionKey: string | null; valueNumber: number | null; valueText: string | null }
export interface EditQuestion { key: string; logicalKey?: string; sectionKey: string; text: string; type: QuestionType; isRequired: boolean; helpText: string; options: EditOption[]; attention?: EditAttention | null }
export interface EditSection { key: string; title: string; description: string }
export interface EditCondition {
  key: string;
  targetType: 'QUESTION' | 'SECTION';
  targetKey: string;
  sourceQuestionKey: string;
  operator: ConditionOperator;
  valueOptionKey: string | null;
  valueNumber: number | null;
  valueText: string | null;
}
export interface EditModel { sections: EditSection[]; questions: EditQuestion[]; conditions: EditCondition[] }

export const usesOptions = (t: QuestionType): boolean => t === 'SINGLE_SELECT' || t === 'MULTI_SELECT';

// Reordenamiento inmutable dentro de un arreglo (para los botones accesibles up/down).
export function moveInArray<T>(arr: T[], index: number, dir: -1 | 1): T[] {
  const j = index + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[index], copy[j]] = [copy[j]!, copy[index]!];
  return copy;
}

export function draftToModel(draft: FormDraft): EditModel {
  return {
    sections: draft.sections.map((s) => ({ key: s.id, title: s.title, description: s.description ?? '' })),
    questions: draft.questions.map((q) => ({
      key: q.id,
      logicalKey: q.logicalKey,
      sectionKey: q.sectionId,
      text: q.text,
      type: q.type,
      isRequired: q.isRequired,
      helpText: q.helpText ?? '',
      options: q.options.map((o) => ({ key: o.id, logicalKey: o.logicalKey, label: o.label })),
      attention: q.attention
        ? {
            operator: q.attention.operator,
            // el backend devuelve valueOptionId (id real de la opcion); en el modelo local la key de la
            // opcion ES su id (o.id), asi que referimos por ese mismo valor.
            valueOptionKey: q.attention.valueOptionKey ?? null,
            valueNumber: q.attention.valueNumber ?? null,
            valueText: q.attention.valueText ?? null,
          }
        : null,
    })),
    conditions: draft.conditions.map((c) => ({
      key: c.id,
      targetType: c.targetType,
      targetKey: (c.targetType === 'QUESTION' ? c.targetQuestionId : c.targetSectionId) ?? '',
      sourceQuestionKey: c.sourceQuestionId,
      operator: c.operator,
      valueOptionKey: c.valueOptionId,
      valueNumber: c.valueNumber,
      valueText: c.valueText,
    })),
  };
}

export function modelToSaveInput(model: EditModel, expectedUpdatedAt: string | undefined): SaveDraftInput {
  const condFor = (type: 'QUESTION' | 'SECTION', key: string): SaveConditionInput[] =>
    model.conditions
      .filter((c) => c.targetType === type && c.targetKey === key)
      .map((c) => ({ sourceQuestionKey: c.sourceQuestionKey, operator: c.operator, valueOptionKey: c.valueOptionKey, valueNumber: c.valueNumber, valueText: c.valueText }));
  return {
    expectedUpdatedAt,
    sections: model.sections.map((s) => ({
      key: s.key,
      title: s.title.trim(),
      description: s.description.trim() || null,
      conditions: condFor('SECTION', s.key),
      questions: model.questions
        .filter((q) => q.sectionKey === s.key)
        .map((q) => ({
          key: q.key,
          logicalKey: q.logicalKey ?? null,
          text: q.text.trim(),
          type: q.type,
          isRequired: q.isRequired,
          helpText: q.helpText.trim() || null,
          options: usesOptions(q.type) ? q.options.map((o) => ({ key: o.key, logicalKey: o.logicalKey ?? null, label: o.label.trim() })) : [],
          conditions: condFor('QUESTION', q.key),
          attention: attentionForSave(q),
        })),
    })),
  };
}

// Origenes validos para una condicion (regla de dependencia HACIA ATRAS, espejo del backend): para una
// pregunta objetivo, solo preguntas anteriores en orden logico; para una seccion objetivo, solo
// preguntas de secciones anteriores. Ademas, solo tipos que admiten operadores (no DATE/TIME).
export function availableConditionSources(model: EditModel, targetType: 'QUESTION' | 'SECTION', targetKey: string): EditQuestion[] {
  const ordered: EditQuestion[] = [];
  for (const s of model.sections) for (const q of model.questions.filter((x) => x.sectionKey === s.key)) ordered.push(q);
  const withOperators = (q: EditQuestion) => OPERATORS_BY_SOURCE_TYPE[q.type].length > 0;
  if (targetType === 'QUESTION') {
    const idx = ordered.findIndex((q) => q.key === targetKey);
    return ordered.slice(0, idx < 0 ? ordered.length : idx).filter(withOperators);
  }
  const secIdx = model.sections.findIndex((s) => s.key === targetKey);
  const earlier = new Set(model.sections.slice(0, secIdx < 0 ? 0 : secIdx).map((s) => s.key));
  return ordered.filter((q) => earlier.has(q.sectionKey) && withOperators(q));
}

export const operatorsForType = (type: QuestionType): readonly ConditionOperator[] => OPERATORS_BY_SOURCE_TYPE[type];

// --- P-061: tiendas que se envian al CREAR un formulario ---
// No enviar tiendas significa "todas las tiendas del negocio", y eso solo lo puede pedir el OWNER: el
// backend lo rechaza para cualquier otro rol. El alta no tiene selector de tiendas, asi que antes un
// MANAGER creaba siempre un formulario para todo el negocio. Ahora se envia la lista explicita de sus
// tiendas activas (las que la pantalla ya cargo, que son las que tiene asignadas). Para el OWNER no se
// envia nada, igual que siempre. El alcance se puede afinar despues en la pestana de configuracion.
export function storeIdsForNewForm(role: string | undefined, stores: ReadonlyArray<{ id: string; isActive: boolean }>): string[] | undefined {
  if (role === 'OWNER') return undefined;
  return stores.filter((store) => store.isActive).map((store) => store.id);
}

// --- Fase 23C: regla de atension en el constructor ---
export const attentionOperatorsForType = (type: QuestionType): readonly ConditionOperator[] => ATTENTION_OPERATORS_BY_TYPE[type];
export const supportsAttention = (type: QuestionType): boolean => ATTENTION_OPERATORS_BY_TYPE[type].length > 0;

// Regla por defecto al activar la casilla, con el primer operador valido del tipo.
export function defaultAttention(type: QuestionType): EditAttention | null {
  const ops = ATTENTION_OPERATORS_BY_TYPE[type];
  if (ops.length === 0) return null;
  return { operator: ops[0]!, valueOptionKey: null, valueNumber: null, valueText: type === 'YES_NO' ? 'true' : null };
}

// Arma el sub-payload de atension para guardar. Devuelve null si el tipo no admite regla o esta apagada.
// Nota: no validamos aqui (el backend es autoritativo); solo mapeamos valores segun el operador.
function attentionForSave(q: EditQuestion): AttentionRuleDraft | null {
  const a = q.attention;
  if (!a || !supportsAttention(q.type)) return null;
  const usesOption = q.type === 'SINGLE_SELECT' || q.type === 'MULTI_SELECT';
  const usesNumber = q.type === 'NUMBER';
  const usesText = q.type === 'YES_NO'; // YES_NO transporta 'true'/'false' en valueText
  return {
    operator: a.operator,
    valueOptionKey: usesOption ? a.valueOptionKey : null,
    valueNumber: usesNumber ? a.valueNumber : null,
    valueText: usesText ? a.valueText : null,
  };
}
